import { useEffect, useState } from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';
import { FONT_WEIGHTS, TEXT_SIZES, COLORS } from '../../../utils/constants';
import styled from 'styled-components';
import { Breadcrumb, Col, Flex, Row, Typography } from 'antd';
import { FacilityDetailType } from '../../../types/types';
import SportsSection from '../FacilitySport/SportsSection';
import BookingsSection from '../FacilityBookings/BookingsSection';
import OverviewSection from './OverviewSection';
import {
  getFacilityByIdRequest,
  updateFacilityStatusRequest,
} from '../../../api/requests/facility';
import { useNavigate, useParams } from 'react-router-dom';
import { FACILITYMANAGEMENTPATH } from '../../../routes/routePaths';
import Spinner from '../../common/Spinner';
import { FacilityStatus } from '../../../enums/enums';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';

import CustomButton from '../../common/Button';
import ApproveFacilityModal from '../Facility/ApproveFacilityModal';

type TabKey = 1 | 2 | 3;

type TabItem = {
  key: TabKey;
  label: string;
};

const TAB_ITEMS: TabItem[] = [
  { key: 1, label: 'Overview' },
  { key: 2, label: 'Sports & Courts' },
  { key: 3, label: 'All Bookings' },
];

const FacilityDetailView = () => {
  const snackbar = useSnackbar();
  const { facilityId } = useParams<{ facilityId: string }>();
  const selectedFacilityId = Number(facilityId);
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<TabKey>(1);
  const [facilityData, setFacilityData] = useState<FacilityDetailType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<boolean>(false);
  const [openApproveFacilityModal, setOpenApproveFacilityModal] =
    useState<boolean>(false);
  const [isContentLoading, setIsContentLoading] = useState<boolean>(false);

  const getFacilityData = async () => {
    try {
      setIsLoading(true);
      const response = await getFacilityByIdRequest(selectedFacilityId, true);
      if (response?.value) {
        setFacilityData(response?.value);
      }
    } catch (error) {
      console.error('Error fetching facility data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmApproveFacility = async () => {
    if (!facilityId) return;
    try {
      const response = await updateFacilityStatusRequest({
        facilityId: Number(facilityId),
        facilityStatus: FacilityStatus.Approved,
      });
      if (response.isSuccess) {
        setAccepted(true);
        snackbar.success('Successfully updated facility status');
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error while updating facility status');
    }
  };

  useEffect(() => {
    getFacilityData();
  }, [selectedFacilityId]);

  const handleReturnToFacilities = () => {
    navigate(`${FACILITYMANAGEMENTPATH}`, {
      state: { from: 'facilityDetailView' },
    });
  };

  const renderTabContent = (tabKey: TabKey) => {
    switch (tabKey) {
      case 1:
        return <OverviewSection facilityData={facilityData!} />;
      case 2:
        return (
          <SportsSection
            facilityId={selectedFacilityId}
            facilityName={facilityData?.facilityName || ''}
            onLoadingChange={setIsContentLoading}
          />
        );
      case 3:
        return <BookingsSection onLoadingChange={setIsContentLoading} />;
      default:
        return null;
    }
  };

  return (
    <FacilityDetails>
      <Breadcrumb>
        <StyledBreadcrumbItem onClick={handleReturnToFacilities}>
          Facility Management
        </StyledBreadcrumbItem>
        <Breadcrumb.Item>{facilityData?.facilityName}</Breadcrumb.Item>
      </Breadcrumb>
      <div>
        <TitleWrapper>
          <div>
            <TitleTypography>{facilityData?.facilityName}</TitleTypography>
            <SubText>Facility Details & Management</SubText>
          </div>
          {facilityData?.facilityStatus === 0 && (
            <CustomButton
              icon={accepted && <StyledCircleCheckIcon />}
              onClick={() => setOpenApproveFacilityModal(true)}
              type="primary"
              disabled={accepted}
            >
              {accepted ? 'Approved' : 'Approve'}
            </CustomButton>
          )}
        </TitleWrapper>
        <TabsWrapper justify="space-between">
          <TabItemsRow>
            {TAB_ITEMS.map((tab: TabItem) => (
              <TabText
                key={tab.key}
                isSeleted={tab.key === selectedTab}
                disabled={isLoading || isContentLoading}
                onClick={() => {
                  if (!isLoading && !isContentLoading) setSelectedTab(tab.key);
                }}
              >
                {tab.label}
              </TabText>
            ))}
          </TabItemsRow>
        </TabsWrapper>
        {isLoading ? (
          <Spinner fullScreen={true} minHeight={200} />
        ) : (
          renderTabContent(selectedTab)
        )}
      </div>
      <ApproveFacilityModal
        open={openApproveFacilityModal}
        onCancel={() => setOpenApproveFacilityModal(false)}
        onConfirm={handleConfirmApproveFacility}
      />
    </FacilityDetails>
  );
};

const FacilityDetails = styled(Col)`
  background: ${COLORS.white.primary};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TitleTypography = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.LG};
  font-weight: ${FONT_WEIGHTS.BOLD};
  display: block;
`;

const SubText = styled(Typography.Text)`
  color: ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.MD};
`;

const TabItemsRow = styled(Row)`
  margin-top: 20px;
  gap: 24px;
`;

const TabText = styled(Typography.Text)<{ isSeleted: boolean }>`
  color: ${({ isSeleted }) =>
    isSeleted ? `${COLORS.skyBlue.primary}` : `${COLORS.grey.primary}`};
  font-weight: ${FONT_WEIGHTS.REGULAR};
  cursor: pointer;
  border-bottom: ${({ isSeleted }) =>
    isSeleted ? `2px solid ${COLORS.skyBlue.primary}` : ''};
`;

const StyledBreadcrumbItem = styled(Breadcrumb.Item)`
  &.ant-breadcrumb-link:hover {
    cursor: pointer;
    color: ${COLORS.skyBlue.primary};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TabsWrapper = styled(Flex)`
  margin-bottom: 16px;
`;

const StyledCircleCheckIcon = styled(CheckCircleOutlined)`
  font-size: ${TEXT_SIZES.LG};
`;

export default FacilityDetailView;
